import { Injectable } from '@angular/core';

import { WarningStatusType } from '@dpr-details/enums';
import { DprActivityGridData } from '@dpr-details/models/grid';
import { ProjectSetup } from '@shared/models';
import { DprActivityCustomData } from '@shared/models/dpr';
import { ActivityMobilisation, CustomData } from '@shared/models/project-setup';
import {
  compareTime,
  convertTimeToDate,
  hasValue,
  isDefaultFinishTime,
  isDefaultStartTime,
} from '@shared/utils';

@Injectable()
export class DprActivityStatusService {
  getStatus(
    row: DprActivityGridData,
    allRows: DprActivityGridData[],
    projectSetup: ProjectSetup,
    isDescendingOrder: boolean
  ): WarningStatusType {
    if (!row) {
      return null;
    }

    if (
      !row.activityGroupId ||
      !row.activityId ||
      !row.locationId ||
      !row.start ||
      !row.finish
    ) {
      return WarningStatusType.RequiredFields;
    }

    const customDataStatus = this.computeStatusCustomDataBased(
      row,
      projectSetup
    );
    if (customDataStatus != null) {
      return customDataStatus;
    }

    const timeStatus = this.computeStatusTimeBased(
      row,
      allRows,
      projectSetup,
      isDescendingOrder
    );
    if (timeStatus != null) {
      return timeStatus;
    }

    return null;
  }

  //#region Private methods

  private computeStatusCustomDataBased(
    row: DprActivityGridData,
    projectSetup: ProjectSetup
  ): WarningStatusType {
    const mandatoryActivityMobilisations =
      this.getMandatoryActivityMobilisations(
        row.activityId,
        row.parallelActivityType,
        projectSetup.activityMobilisationList
      );

    const completed = mandatoryActivityMobilisations.every((x) => {
      return this.isValueSetForMandatoryCustomData(
        row.dprActivityCustomDataList,
        x.customDataId,
        projectSetup.customDataList
      );
    });

    return !completed ? WarningStatusType.RequiredCustomData : null;
  }

  private getMandatoryActivityMobilisations(
    activityId: number,
    parallelActivityType: number,
    activityMobilisations: ActivityMobilisation[]
  ): ActivityMobilisation[] {
    return activityMobilisations.filter(
      (a) =>
        // the conversion to 0 in case of undefined is done due to
        // parallelActivityType being 0 for newly created activities and undefined for
        // those fetched from the backend
        a.activityId === activityId &&
        (a.parallelActivityType ?? 0) === (parallelActivityType ?? 0) &&
        a.isActive &&
        a.isMandatory
    );
  }

  private isValueSetForMandatoryCustomData(
    dprActivityCustomDataList: DprActivityCustomData[],
    customDataId: number,
    customDataList: CustomData[]
  ): boolean {
    const customData = customDataList?.find((x) => x.id === customDataId);

    return dprActivityCustomDataList.some(
      (x) =>
        x.customDataId === customDataId &&
        hasValue(x.value, customData?.dataTypeId)
    );
  }

  private computeStatusTimeBased(
    current: DprActivityGridData,
    allRows: DprActivityGridData[],
    projectSetup: ProjectSetup,
    isDescendingOrder: boolean
  ): WarningStatusType {
    const activityStream = projectSetup.activityStreams.find(
      (x) =>
        (x.parallelActivityType ?? 0) === (current.parallelActivityType ?? 0)
    );

    const currentRowIndex = allRows.findIndex((x) => x.id === current.id);

    const previous =
      allRows[isDescendingOrder ? currentRowIndex + 1 : currentRowIndex - 1];
    const next =
      allRows[isDescendingOrder ? currentRowIndex - 1 : currentRowIndex + 1];
    const allExistingRows = allRows.filter((row) => row.id !== current.id);
    const previousExistingRows = allRows.filter((row) =>
      isDescendingOrder
        ? allRows.indexOf(row) > allRows.indexOf(current)
        : allRows.indexOf(row) < allRows.indexOf(current)
    );

    // start or finish not completed
    if (!current.start || !current.finish) {
      return WarningStatusType.RequiredFields;
    }

    // start is bigger than finish
    if (this.compareTime(current.start, current.finish) > 0) {
      return WarningStatusType.ErrorInTime;
    }

    // no gaps are allowed
    if (activityStream.is24hRequired) {
      // first time must be start of day
      if (!previous && !this.isDefaultStartTime(current.start)) {
        return WarningStatusType.ErrorInTime;
      }

      // one finish time from the list must be end of day (report the error on the last line)
      if (
        !next &&
        allRows.length &&
        !allRows.some((x) => this.isDefaultFinishTime(x.finish))
      ) {
        return WarningStatusType.ErrorInTime;
      }

      // check that there are no gaps, the entire 24 h interval is covered
      // check if there are no intervals in the previous existing rows that include the current start time,
      // if the current start is not 00:00
      if (
        previousExistingRows.length &&
        !this.isDefaultStartTime(current.start) &&
        !this.intervalsIncludeCurrentTime(previousExistingRows, current.start)
      ) {
        return WarningStatusType.ErrorInTime;
      }

      // check if there are no intervals that include the current finish time, if the current finish is not 23:59
      if (
        allExistingRows.length &&
        !this.isDefaultFinishTime(current.finish) &&
        !this.intervalsIncludeCurrentTime(allExistingRows, current.finish)
      ) {
        return WarningStatusType.ErrorInTime;
      }
    }

    if (activityStream.isNoTimeOverlap && allExistingRows.length) {
      // check that there are finish times that overlap the current start time
      if (
        !this.isDefaultStartTime(current.start) &&
        this.finishTimesOverlapCurrentStart(allExistingRows, current)
      ) {
        return WarningStatusType.ErrorInTime;
      }

      // check that there are start times that overlap the current finish time
      if (
        !this.isDefaultFinishTime(current.finish) &&
        this.startTimesOverlapCurrentFinish(allExistingRows, current)
      ) {
        return WarningStatusType.ErrorInTime;
      }

      // check for duplicate time intervals
      if (
        !this.isMilestoneTime(current) &&
        this.currentRowIsDuplicate(allExistingRows, current)
      ) {
        return WarningStatusType.ErrorInTime;
      }
    }

    return null;
  }

  private compareTime(start: string, finish: string): number {
    return compareTime(convertTimeToDate(start), convertTimeToDate(finish));
  }

  private isDefaultStartTime(time: string): boolean {
    return isDefaultStartTime(convertTimeToDate(time));
  }

  private isDefaultFinishTime(time: string): boolean {
    return isDefaultFinishTime(convertTimeToDate(time));
  }

  private intervalsIncludeCurrentTime(
    existingRows: DprActivityGridData[],
    currentTime: string
  ): boolean {
    return existingRows.some(
      (row) =>
        this.compareTime(row.start, currentTime) <= 0 &&
        this.compareTime(row.finish, currentTime) >= 0
    );
  }

  private finishTimesOverlapCurrentStart(
    existingRows: DprActivityGridData[],
    current: DprActivityGridData
  ): boolean {
    // at least one of the finish times from the list, before the current row, is equal or larger than the current start time,
    // if the current row is not the first one
    return existingRows.some(
      (row) =>
        this.compareTime(row.finish, current.start) > 0 &&
        this.compareTime(row.start, current.start) < 0
    );
  }

  private startTimesOverlapCurrentFinish(
    existingRows: DprActivityGridData[],
    current: DprActivityGridData
  ): boolean {
    // at least one of the start times from the list, after the current row, is equal or smaller than the current finish time,
    // if the current row is not the last one
    return existingRows.some(
      (row) =>
        this.compareTime(row.start, current.finish) < 0 &&
        this.compareTime(row.finish, current.finish) > 0
    );
  }

  private isMilestoneTime(current: DprActivityGridData): boolean {
    return this.compareTime(current.start, current.finish) === 0;
  }

  private currentRowIsDuplicate(
    existingRows: DprActivityGridData[],
    current: DprActivityGridData
  ): boolean {
    return existingRows.some(
      (row) =>
        this.compareTime(row.start, current.start) === 0 &&
        this.compareTime(row.finish, current.finish) === 0
    );
  }

  //#endregion
}
